/**
 * Estimates API Service
 * Provides data for historical estimates with hierarchical cost data
 * Supports mock and real API modes
 */

import { USE_MOCK_DATA } from './mockData';
import api from './api';

/**
 * Convert snake_case string to camelCase
 */
function snakeToCamel(str) {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Recursively convert all keys in an object from snake_case to camelCase
 */
function convertKeysToCamelCase(obj) {
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToCamelCase);
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = snakeToCamel(key);
      result[camelKey] = convertKeysToCamelCase(obj[key]);
      return result;
    }, {});
  }
  return obj;
}

// Mock data for estimates
const mockEstimates = [
  {
    estimateId: 'est-001',
    projectName: 'Whole Foods Market - Oakley',
    clientName: 'Whole Foods Market Inc.',
    jobType: 'Retail',
    squareFootage: 45000,
    location: 'Cincinnati, OH',
    constructionType: 'Steel Frame',
    stories: 1,
    prevailingWage: false,
    totalCost: 4500000,
    costPerSf: 100,
    estimateDate: '2024-06-15',
    completedDate: '2024-12-20',
    projectCompleted: true,
    actualCost: 4650000,
    costVariancePercent: 3.3,
    sourceFile: 'WF_Oakley_Estimate.xlsx',
    categories: [
      {
        categoryId: 'cat-001',
        categoryCode: 'A',
        categoryName: 'General Conditions',
        subtotal: 360000,
        costPerSf: 8.0,
        lineItems: [
          { lineItemId: 'li-001', description: 'Project Management', totalCost: 180000, unit: 'LS' },
          { lineItemId: 'li-002', description: 'Site Supervision', totalCost: 120000, unit: 'LS' },
          { lineItemId: 'li-003', description: 'Temporary Facilities', totalCost: 60000, unit: 'LS' },
        ],
      },
      {
        categoryId: 'cat-002',
        categoryCode: 'B',
        categoryName: 'Site Work',
        subtotal: 450000,
        costPerSf: 10.0,
        lineItems: [
          { lineItemId: 'li-004', description: 'Excavation & Grading', totalCost: 180000, quantity: 45000, unit: 'SF', unitCost: 4.0 },
          { lineItemId: 'li-005', description: 'Utilities', totalCost: 150000, unit: 'LS' },
          { lineItemId: 'li-006', description: 'Paving & Striping', totalCost: 120000, quantity: 2000, unit: 'SY', unitCost: 60.0 },
        ],
      },
      {
        categoryId: 'cat-003',
        categoryCode: 'C',
        categoryName: 'Concrete',
        subtotal: 540000,
        costPerSf: 12.0,
        lineItems: [
          { lineItemId: 'li-007', description: 'Foundations', totalCost: 270000, unit: 'LS' },
          { lineItemId: 'li-008', description: 'Slab on Grade', totalCost: 180000, quantity: 45000, unit: 'SF', unitCost: 4.0 },
          { lineItemId: 'li-009', description: 'Sidewalks', totalCost: 90000, unit: 'LS' },
        ],
      },
      {
        categoryId: 'cat-004',
        categoryCode: 'D',
        categoryName: 'Steel',
        subtotal: 675000,
        costPerSf: 15.0,
        lineItems: [
          { lineItemId: 'li-010', description: 'Structural Steel', totalCost: 540000, quantity: 120, unit: 'TON', unitCost: 4500.0 },
          { lineItemId: 'li-011', description: 'Miscellaneous Metals', totalCost: 135000, unit: 'LS' },
        ],
      },
      {
        categoryId: 'cat-005',
        categoryCode: 'E',
        categoryName: 'MEP',
        subtotal: 1125000,
        costPerSf: 25.0,
        lineItems: [
          { lineItemId: 'li-012', description: 'HVAC', totalCost: 450000, unit: 'LS' },
          { lineItemId: 'li-013', description: 'Electrical', totalCost: 405000, unit: 'LS' },
          { lineItemId: 'li-014', description: 'Plumbing', totalCost: 180000, unit: 'LS' },
          { lineItemId: 'li-015', description: 'Fire Protection', totalCost: 90000, unit: 'LS' },
        ],
      },
    ],
  },
  {
    estimateId: 'est-002',
    projectName: 'Amazon Distribution Center',
    clientName: 'Amazon Logistics',
    jobType: 'Warehouse',
    squareFootage: 250000,
    location: 'Columbus, OH',
    constructionType: 'Tilt-Up',
    stories: 1,
    prevailingWage: true,
    totalCost: 22500000,
    costPerSf: 90,
    estimateDate: '2024-03-10',
    completedDate: null,
    projectCompleted: false,
    actualCost: null,
    sourceFile: 'Amazon_DC_Columbus.xlsx',
    categories: [
      {
        categoryId: 'cat-006',
        categoryCode: 'A',
        categoryName: 'General Conditions',
        subtotal: 1800000,
        costPerSf: 7.2,
        lineItems: [
          { lineItemId: 'li-016', description: 'Project Management', totalCost: 900000, unit: 'LS' },
          { lineItemId: 'li-017', description: 'Field Supervision', totalCost: 600000, unit: 'LS' },
          { lineItemId: 'li-018', description: 'Temp Facilities & Equipment', totalCost: 300000, unit: 'LS' },
        ],
      },
      {
        categoryId: 'cat-007',
        categoryCode: 'B',
        categoryName: 'Site Work',
        subtotal: 2250000,
        costPerSf: 9.0,
        lineItems: [
          { lineItemId: 'li-019', description: 'Mass Grading', totalCost: 1000000, quantity: 15, unit: 'ACS', unitCost: 66666.67 },
          { lineItemId: 'li-020', description: 'Storm Drainage', totalCost: 500000, unit: 'LS' },
          { lineItemId: 'li-021', description: 'Parking & Drives', totalCost: 750000, quantity: 50000, unit: 'SF', unitCost: 15.0 },
        ],
      },
      {
        categoryId: 'cat-008',
        categoryCode: 'C',
        categoryName: 'Concrete',
        subtotal: 4500000,
        costPerSf: 18.0,
        lineItems: [
          { lineItemId: 'li-022', description: 'Tilt-Up Wall Panels', totalCost: 2500000, quantity: 250000, unit: 'SF', unitCost: 10.0 },
          { lineItemId: 'li-023', description: 'Floor Slab', totalCost: 1500000, quantity: 250000, unit: 'SF', unitCost: 6.0 },
          { lineItemId: 'li-024', description: 'Dock Equipment Pads', totalCost: 500000, unit: 'LS' },
        ],
      },
    ],
  },
  {
    estimateId: 'est-003',
    projectName: 'Medical Office Building',
    clientName: 'TriHealth Systems',
    jobType: 'Medical Office',
    squareFootage: 35000,
    location: 'Cincinnati, OH',
    constructionType: 'Steel Frame',
    stories: 3,
    prevailingWage: false,
    totalCost: 5250000,
    costPerSf: 150,
    estimateDate: '2024-08-01',
    completedDate: '2025-01-10',
    projectCompleted: true,
    actualCost: 5100000,
    costVariancePercent: -2.9,
    sourceFile: 'TriHealth_MOB.xlsx',
    categories: [],
  },
];

const mockApiDelay = () => new Promise(resolve => setTimeout(resolve, 300));

// Contractor fee per square foot: base total (sum of non-fee category
// subtotals) x fee % (held in the fee category code '00' subtotal) / sqft.
// Mirrors the server-side computation in list_estimates.
const computeMockFeePerSf = (estimate) => {
  if (estimate.feePerSf != null) return estimate.feePerSf;
  const categories = estimate.categories || [];
  const feeCat = categories.find(c => (c.categoryCode || c.category_code) === '00');
  const feePct = Number(feeCat?.subtotal) || 0;
  const sqFt = Number(estimate.squareFootage) || 0;
  if (!feePct || !sqFt) return null;
  const baseTotal = categories
    .filter(c => (c.categoryCode || c.category_code) !== '00')
    .reduce((sum, c) => sum + (Number(c.subtotal) || 0), 0);
  return (baseTotal * (feePct / 100)) / sqFt;
};

const mockSortComparators = {
  projectName: (a, b) => (a.projectName || '').localeCompare(b.projectName || ''),
  jobType: (a, b) => (a.jobType || '').localeCompare(b.jobType || ''),
  location: (a, b) => (a.location || '').localeCompare(b.location || ''),
  squareFootage: (a, b) => (a.squareFootage || 0) - (b.squareFootage || 0),
  totalCost: (a, b) => (a.totalCost || 0) - (b.totalCost || 0),
  costPerSf: (a, b) => (a.costPerSf || 0) - (b.costPerSf || 0),
  feePerSf: (a, b) => (a.feePerSf || 0) - (b.feePerSf || 0),
  documentCount: (a, b) => (a.documentCount || 0) - (b.documentCount || 0),
  status: (a, b) => (a.projectCompleted === b.projectCompleted ? 0 : a.projectCompleted ? 1 : -1),
  estimateDate: (a, b) => String(a.estimateDate || '').localeCompare(String(b.estimateDate || '')),
};

const mockEstimatesService = {
  async getEstimates(options = {}) {
    await mockApiDelay();

    let filtered = mockEstimates.map(e => ({ ...e, feePerSf: computeMockFeePerSf(e) }));

    // Apply filters
    if (options.jobType) {
      filtered = filtered.filter(e => e.jobType === options.jobType);
    }
    if (options.location) {
      filtered = filtered.filter(e => e.location.includes(options.location));
    }
    if (options.search) {
      const searchLower = options.search.toLowerCase();
      filtered = filtered.filter(e =>
        e.projectName.toLowerCase().includes(searchLower) ||
        e.clientName.toLowerCase().includes(searchLower)
      );
    }
    if (options.completedOnly) {
      filtered = filtered.filter(e => e.projectCompleted);
    }

    // Sorting (defaults to estimateDate desc, matching the backend)
    const comparator = mockSortComparators[options.sortBy] || mockSortComparators.estimateDate;
    const dir = (options.sortDir || (options.sortBy ? 'asc' : 'desc')) === 'asc' ? 1 : -1;
    filtered.sort((a, b) => comparator(a, b) * dir);

    // Pagination
    const page = options.page || 1;
    const limit = options.limit || 10;
    const start = (page - 1) * limit;
    const paginated = filtered.slice(start, start + limit);

    return {
      estimates: paginated,
      total: filtered.length,
      page,
      totalPages: Math.ceil(filtered.length / limit),
      jobTypes: ['Retail', 'Warehouse', 'Medical Office', 'Office', 'Industrial', 'Education'],
    };
  },

  async getEstimateById(estimateId) {
    await mockApiDelay();
    const estimate = mockEstimates.find(e => e.estimateId === estimateId);
    if (!estimate) {
      throw new Error('Estimate not found');
    }
    return estimate;
  },

  async searchSimilarEstimates(query) {
    await mockApiDelay();
    // Filter to only completed estimates when statusFilter is 'complete'
    let filteredEstimates = mockEstimates;
    if (query.statusFilter === 'complete') {
      filteredEstimates = mockEstimates.filter(e => e.status === 'complete' || e.projectCompleted);
    }
    // Return mock similar estimates with scores
    return {
      results: filteredEstimates.slice(0, 3).map((e, i) => ({
        ...e,
        hybridScore: 0.95 - (i * 0.1),
        semanticScore: 0.90 - (i * 0.08),
        scoreBreakdown: {
          jobTypeMatch: 0.15,
          sizeSimilarity: 0.12 - (i * 0.02),
          locationSimilarity: 0.10,
          semanticSimilarity: 0.40 - (i * 0.05),
        },
      })),
      query,
    };
  },

  async searchSimilarLineItems(description, categoryName) {
    await mockApiDelay();
    // Return mock similar line items
    const allLineItems = mockEstimates.flatMap(e =>
      e.categories?.flatMap(c =>
        c.lineItems?.map(li => ({
          ...li,
          categoryName: c.categoryName,
          projectName: e.projectName,
          jobType: e.jobType,
        })) || []
      ) || []
    );

    return {
      results: allLineItems.slice(0, 5).map((li, i) => ({
        ...li,
        similarityScore: 0.90 - (i * 0.1),
      })),
    };
  },

  async createEstimate(estimateData) {
    await mockApiDelay();
    // Generate a new estimate ID
    const newEstimateId = `est-${String(mockEstimates.length + 1).padStart(3, '0')}`;
    const newEstimate = {
      estimateId: newEstimateId,
      projectName: estimateData.projectName,
      clientName: estimateData.clientName || 'New Client',
      jobType: estimateData.jobType || 'General',
      squareFootage: estimateData.squareFootage || 0,
      location: estimateData.location || 'Unknown',
      constructionType: estimateData.constructionType || 'General',
      stories: estimateData.stories || 1,
      prevailingWage: estimateData.prevailingWage || false,
      totalCost: estimateData.totalCost || 0,
      costPerSf: estimateData.costPerSf || 0,
      estimateDate: new Date().toISOString().split('T')[0],
      completedDate: null,
      projectCompleted: false,
      actualCost: null,
      costVariancePercent: null,
      sourceFile: null,
      categories: estimateData.categories || [],
    };

    // Add to mock data
    mockEstimates.push(newEstimate);

    return newEstimate;
  },

  async getNearestNeighborsForLineItem(estimateId, categoryId, lineItemId) {
    await mockApiDelay();
    // Mock nearest neighbors data
    return {
      line_item: {
        line_item_id: lineItemId,
        description: 'Source line item',
      },
      neighbors: [
        {
          line_item_id: 'neighbor-1',
          estimate_id: 'est-001',
          project_name: 'Whole Foods Market - Denver',
          description: 'Interior latex paint - Premium grade',
          total_cost: 45000,
          unit_cost: 2.50,
          quantity: 18000,
          unit: 'SF',
          similarity_score: 0.87,
          similarity_breakdown: {
            semantic: 0.82,
            location_similarity: 0.75,
            date_recency: 0.90,
            size_match: 0.88,
            category_specific: {
              paint_grade_match: 1.0,
              paint_color_match: 0.9,
              surface_type_match: 0.8,
            },
          },
          cost_variance: '+8%',
          project_date: '2024-03-15',
        },
        {
          line_item_id: 'neighbor-2',
          estimate_id: 'est-002',
          project_name: 'Home Depot Distribution - Phoenix',
          description: 'Interior paint - Standard finish',
          total_cost: 38000,
          unit_cost: 2.20,
          quantity: 17000,
          unit: 'SF',
          similarity_score: 0.79,
          similarity_breakdown: {
            semantic: 0.75,
            location_similarity: 0.68,
            date_recency: 0.85,
            size_match: 0.92,
            category_specific: {
              paint_grade_match: 0.7,
              paint_color_match: 0.85,
              surface_type_match: 0.9,
            },
          },
          cost_variance: '-4%',
          project_date: '2024-01-20',
        },
        {
          line_item_id: 'neighbor-3',
          estimate_id: 'est-003',
          project_name: 'Office Building - Seattle',
          description: 'Interior latex paint - Commercial grade',
          total_cost: 52000,
          unit_cost: 2.75,
          quantity: 19000,
          unit: 'SF',
          similarity_score: 0.72,
          similarity_breakdown: {
            semantic: 0.78,
            location_similarity: 0.55,
            date_recency: 0.95,
            size_match: 0.80,
            category_specific: {
              paint_grade_match: 0.8,
              paint_color_match: 1.0,
              surface_type_match: 0.75,
            },
          },
          cost_variance: '+15%',
          project_date: '2024-05-10',
        },
        {
          line_item_id: 'neighbor-4',
          estimate_id: 'est-004',
          project_name: 'Warehouse - Atlanta',
          description: 'Interior paint application',
          total_cost: 35000,
          unit_cost: 2.10,
          quantity: 16500,
          unit: 'SF',
          similarity_score: 0.68,
          similarity_breakdown: {
            semantic: 0.70,
            location_similarity: 0.62,
            date_recency: 0.80,
            size_match: 0.75,
            category_specific: {
              paint_grade_match: 0.6,
              paint_color_match: 0.8,
              surface_type_match: 0.85,
            },
          },
          cost_variance: '-8%',
          project_date: '2023-11-05',
        },
        {
          line_item_id: 'neighbor-5',
          estimate_id: 'est-005',
          project_name: 'Data Center - Dallas',
          description: 'Interior painting - High-performance coating',
          total_cost: 58000,
          unit_cost: 3.00,
          quantity: 19500,
          unit: 'SF',
          similarity_score: 0.65,
          similarity_breakdown: {
            semantic: 0.68,
            location_similarity: 0.58,
            date_recency: 0.75,
            size_match: 0.85,
            category_specific: {
              paint_grade_match: 0.5,
              paint_color_match: 0.75,
              surface_type_match: 0.7,
            },
          },
          cost_variance: '+20%',
          project_date: '2023-09-22',
        },
      ],
    };
  },

  async getUploadUrl(fileName) {
    await mockApiDelay();
    const id = `upload-${Date.now()}`;
    return {
      uploadUrl: 'mock://upload',
      uploadId: id,
      estimateId: id,
    };
  },

  async uploadEstimate(file, onProgress) {
    // Simulate upload progress
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(r => setTimeout(r, 200));
      onProgress?.(i);
    }

    return {
      uploadId: `upload-${Date.now()}`,
      status: 'processing',
      fileName: file.name,
    };
  },

  getJobTypes() {
    return ['Retail', 'Warehouse', 'Medical Office', 'Office', 'Industrial', 'Education'];
  },

  async deleteEstimate(estimateId) {
    await mockApiDelay();
    const index = mockEstimates.findIndex(e => e.estimateId === estimateId);
    if (index === -1) {
      throw new Error('Estimate not found');
    }
    mockEstimates.splice(index, 1);
    return { success: true };
  },

  async updateEstimateStatus(estimateId, status) {
    await mockApiDelay();
    const estimate = mockEstimates.find(e => e.estimateId === estimateId);
    if (!estimate) {
      throw new Error('Estimate not found');
    }
    estimate.status = status;
    return { success: true, status };
  },
};

/**
 * Estimates API that automatically uses mock or real data
 */
export const estimatesApi = {
  /**
   * Get list of estimates with filtering and pagination
   */
  getEstimates: async (options = {}) => {
    if (USE_MOCK_DATA) {
      return mockEstimatesService.getEstimates(options);
    }

    const params = new URLSearchParams();
    if (options.jobType) params.append('jobType', options.jobType);
    if (options.location) params.append('location', options.location);
    if (options.search) params.append('search', options.search);
    if (options.status) params.append('status', options.status);
    if (options.completedOnly) params.append('completedOnly', 'true');
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortDir) params.append('sortDir', options.sortDir);

    const query = params.toString();
    return api.get(`/estimates${query ? `?${query}` : ''}`);
  },

  /**
   * Get a single estimate by ID with full hierarchy
   */
  getEstimateById: async (estimateId) => {
    if (USE_MOCK_DATA) {
      return mockEstimatesService.getEstimateById(estimateId);
    }
    const response = await api.get(`/estimates/${estimateId}`);
    return response.data; // Unwrap the data property from {success: true, data: {...}}
  },

  /**
   * Search for similar estimates
   */
  searchSimilarEstimates: async (query) => {
    if (USE_MOCK_DATA) {
      return mockEstimatesService.searchSimilarEstimates(query);
    }
    const response = await api.post('/estimates/search/similar', query);
    // Convert snake_case keys to camelCase for frontend consistency
    const data = response.data;
    if (data && data.results) {
      data.results = convertKeysToCamelCase(data.results);
    }
    return data;
  },

  /**
   * Search for similar line items
   */
  searchSimilarLineItems: async (description, categoryName) => {
    if (USE_MOCK_DATA) {
      return mockEstimatesService.searchSimilarLineItems(description, categoryName);
    }
    return api.post('/estimates/search/line-items', { description, categoryName });
  },

  /**
   * Get nearest neighbors for a specific line item
   */
  getNearestNeighborsForLineItem: async (estimateId, categoryId, lineItemId) => {
    if (USE_MOCK_DATA) {
      return mockEstimatesService.getNearestNeighborsForLineItem(estimateId, categoryId, lineItemId);
    }
    return api.get(`/estimates/${estimateId}/categories/${categoryId}/line-items/${lineItemId}/neighbors`);
  },

  /**
   * Request a presigned URL for estimate upload
   * @param {string} fileName - Name of the file to upload
   * @param {string} contentType - MIME type of the file
   */
  getUploadUrl: async (fileName, contentType = 'text/csv', { skipProcessing = false, projectName = null } = {}) => {
    if (USE_MOCK_DATA) {
      return mockEstimatesService.getUploadUrl(fileName);
    }
    const body = { fileName, contentType };
    if (skipProcessing) body.skipProcessing = true;
    if (projectName) body.projectName = projectName;
    return api.post('/estimates/upload', body);
  },

  /**
   * Upload estimate file
   * @param {File} file - File to upload
   * @param {Function} onProgress - Progress callback
   * @param {Object} options - Upload options
   * @param {boolean} options.skipProcessing - Skip AI parsing (for manual entry)
   */
  uploadEstimate: async (file, onProgress, { skipProcessing = false, projectName = null } = {}) => {
    if (USE_MOCK_DATA) {
      return mockEstimatesService.uploadEstimate(file, onProgress);
    }

    // Determine content type based on file extension
    const extension = file.name.toLowerCase().split('.').pop();
    let contentType = file.type || 'application/octet-stream';

    // Override with explicit types for known extensions (file.type can be unreliable)
    if (extension === 'csv') {
      contentType = 'text/csv';
    } else if (extension === 'xlsx') {
      contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    } else if (extension === 'xls') {
      contentType = 'application/vnd.ms-excel';
    }

    // Get presigned URL from API (response wrapped in { success, data })
    // Backend now creates a pending estimate first and returns estimateId
    const response = await estimatesApi.getUploadUrl(file.name, contentType, { skipProcessing, projectName });
    const { uploadUrl, uploadId, estimateId } = response.data;

    // Upload to S3 using the same content type as the signed URL
    const s3Response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType,
      },
    });

    if (!s3Response.ok) {
      throw new Error(`S3 upload failed with status ${s3Response.status}`);
    }

    // Return estimateId for polling - this is the actual database ID
    return { estimateId: estimateId || uploadId, uploadId, status: 'processing', fileName: file.name };
  },

  /**
   * Get job types for filter dropdown
   * @deprecated Use getEstimates() which now includes jobTypes in response
   */
  getJobTypes: async () => {
    if (USE_MOCK_DATA) {
      return mockEstimatesService.getJobTypes();
    }
    // Job types are now returned by getEstimates(), so just return empty array
    // This method is deprecated and kept for backwards compatibility
    return [];
  },

  /**
   * Update actual cost for completed project
   */
  updateActualCost: async (estimateId, actualCost, explanation) => {
    if (USE_MOCK_DATA) {
      return { success: true };
    }
    return api.patch(`/estimates/${estimateId}/actual-cost`, {
      actualCost,
      costVarianceExplanation: explanation,
    });
  },

  /**
   * Create a new estimate
   */
  createEstimate: async (estimateData) => {
    if (USE_MOCK_DATA) {
      return mockEstimatesService.createEstimate(estimateData);
    }
    const response = await api.post('/estimates', estimateData);
    return response.data; // Unwrap the data property from {success: true, data: {...}}
  },

  /**
   * Update an existing estimate
   */
  updateEstimate: async (estimateId, estimateData) => {
    if (USE_MOCK_DATA) {
      // Mock update - find and update the estimate
      const index = mockEstimates.findIndex(e => e.estimateId === estimateId);
      if (index === -1) {
        throw new Error('Estimate not found');
      }
      mockEstimates[index] = { ...mockEstimates[index], ...estimateData };
      return mockEstimates[index];
    }
    const response = await api.put(`/estimates/${estimateId}`, estimateData);
    return response.data;
  },

  /**
   * Delete an estimate
   */
  deleteEstimate: async (estimateId) => {
    if (USE_MOCK_DATA) {
      return mockEstimatesService.deleteEstimate(estimateId);
    }
    return api.delete(`/estimates/${estimateId}`);
  },

  /**
   * Update estimate status (draft/complete)
   */
  updateEstimateStatus: async (estimateId, status) => {
    if (USE_MOCK_DATA) {
      return mockEstimatesService.updateEstimateStatus(estimateId, status);
    }
    return api.patch(`/estimates/${estimateId}/status`, { status });
  },

  /**
   * Get AI-powered cost estimates for line items
   * @param {Object} projectContext - Project details (jobType, squareFootage, location, prevailingWage)
   * @param {Array} lineItems - Array of line items to estimate (description, categoryName)
   * @param {string} [excludeEstimateId] - Optional estimate ID to exclude from similar items (prevents self-referencing)
   * @returns {Object} estimates - Array of AI estimates with explanations
   */
  estimateLineItems: async (projectContext, lineItems, excludeEstimateId = null, categoryId = null, estimateId = null) => {
    if (USE_MOCK_DATA) {
      // Mock AI estimation response
      // Note: No estimatedCost - total is computed as unitCost × quantity
      return {
        estimates: lineItems.map((item, i) => {
          const unitCost = Math.round(Math.random() * 50 + 10);
          const quantity = Math.round(Math.random() * 10000 + 1000);
          return {
            description: item.description,
            categoryName: item.categoryName || 'General',
            unitCost,
            unit: 'SF',
            quantity,
            confidenceScore: 0.85 - (i * 0.05),
            explanation: `AI estimate based on ${3 + i} similar historical line items. ` +
              `Considering ${projectContext.jobType || 'commercial'} project context, ` +
              `${projectContext.squareFootage?.toLocaleString() || 0} SF in ${projectContext.location || 'unknown location'}. ` +
              `${projectContext.prevailingWage ? 'Prevailing wage rates applied (+25%). ' : ''}` +
              `Historical data shows typical costs ranging from baseline to +15% for this scope.`,
            similarItemIds: [`mock-${i}-1`, `mock-${i}-2`, `mock-${i}-3`],
            priceRange: {
              min: Math.round(unitCost * quantity * 0.8),
              max: Math.round(unitCost * quantity * 1.2),
            },
            estimatedAt: new Date().toISOString(),
          };
        }),
        projectContext,
        totalEstimates: lineItems.length,
      };
    }
    const payload = { projectContext, lineItems };
    if (excludeEstimateId) {
      payload.excludeEstimateId = excludeEstimateId;
    }
    if (categoryId) {
      payload.categoryId = categoryId;
    }
    if (estimateId) {
      // Lets the backend load document-extracted facts for this estimate and
      // surface them in the AI prompt.
      payload.estimateId = estimateId;
    }
    return api.post('/estimates/line-items/estimate', payload);
  },

  /**
   * Estimate all line items in batch, processing by category for efficiency.
   * Each category is sent as a single request with categoryId, enabling the backend
   * to batch similarity searches by MasterFormat code.
   *
   * @param {Object} projectContext - User's project details (jobType, squareFootage, location, prevailingWage)
   * @param {Array} categories - Array of category objects with lineItems:
   *   [{ categoryId, categoryName, lineItems: [{ description, masterformatCode, quantity, unit }] }]
   * @param {Function} onProgress - Callback for progress updates ({total, completed, currentCategory, percentComplete})
   * @param {string} excludeEstimateId - Template ID to exclude from similar items
   * @param {Function} onPartialResults - Optional callback to receive results as each category completes (for skip functionality)
   * @returns {Array} Array of AI estimates for all line items
   */
  estimateLineItemsBatch: async (projectContext, categories, onProgress, excludeEstimateId = null, onPartialResults = null, estimateId = null) => {
    const DELAY_BETWEEN_BATCHES_MS = 300;
    const MAX_RETRIES = 3;
    const MAX_ITEMS_PER_SUBBATCH = 3; // Keep requests under 29s API Gateway timeout

    const results = [];
    const totalItems = categories.reduce((sum, cat) => sum + (cat.lineItems?.length || 0), 0);
    let completedItems = 0;

    // Process each category, splitting into sub-batches if needed
    for (let catIndex = 0; catIndex < categories.length; catIndex++) {
      const category = categories[catIndex];
      const categoryLineItems = category.lineItems || [];

      if (categoryLineItems.length === 0) {
        continue;
      }

      // Report progress before processing category
      if (onProgress) {
        onProgress({
          total: totalItems,
          completed: completedItems,
          currentCategory: category.categoryName,
          percentComplete: Math.round((completedItems / totalItems) * 100),
        });
      }

      // Split category into sub-batches to avoid API Gateway timeout
      const subBatches = [];
      for (let i = 0; i < categoryLineItems.length; i += MAX_ITEMS_PER_SUBBATCH) {
        subBatches.push(categoryLineItems.slice(i, i + MAX_ITEMS_PER_SUBBATCH));
      }

      const categoryResults = [];

      // Process each sub-batch sequentially
      for (let batchIndex = 0; batchIndex < subBatches.length; batchIndex++) {
        const subBatch = subBatches[batchIndex];

        // Format line items for API (include categoryName for each item)
        const formattedLineItems = subBatch.map(item => ({
          description: item.description,
          categoryName: category.categoryName,
          masterformatCode: item.masterformatCode,
          quantity: item.quantity,
          unit: item.unit,
        }));

        // Helper to check if error is a timeout (504 Gateway Timeout or similar)
        const isTimeoutError = (error) => {
          const message = error?.message?.toLowerCase() || '';
          const status = error?.response?.status || error?.status;
          return status === 504 || message.includes('timeout') || message.includes('timed out');
        };

        // Helper to estimate a single item individually
        const estimateSingleItem = async (item) => {
          try {
            const response = await estimatesApi.estimateLineItems(
              projectContext,
              [item],
              excludeEstimateId,
              category.categoryId,
              estimateId
            );
            const estimates = response.estimates || response;
            return estimates[0];
          } catch (err) {
            console.error(`Individual estimation failed for "${item.description}":`, err);
            return {
              description: item.description,
              categoryName: item.categoryName,
              unitCost: null,
              unit: item.unit || 'LS',
              quantity: item.quantity || 1,
              confidenceScore: 0,
              explanation: 'AI estimation failed. Please estimate manually or retry.',
              estimationMethod: 'failed',
              failureReason: err.message,
            };
          }
        };

        // Retry logic for rate limiting, with timeout fallback to individual items
        let retries = 0;
        let subBatchResults = null;
        let shouldFallbackToIndividual = false;

        while (retries < MAX_RETRIES && !subBatchResults && !shouldFallbackToIndividual) {
          try {
            // Send sub-batch request with categoryId for batch mode
            const response = await estimatesApi.estimateLineItems(
              projectContext,
              formattedLineItems,
              excludeEstimateId,
              category.categoryId,  // Enables batch mode in backend
              estimateId
            );
            subBatchResults = response.estimates || response;
          } catch (error) {
            // Check if this is a timeout error - if so, fall back to individual items
            if (isTimeoutError(error)) {
              console.warn(`Timeout for category "${category.categoryName}" (batch ${batchIndex + 1}/${subBatches.length}), falling back to individual item estimation`);
              shouldFallbackToIndividual = true;
            } else {
              retries++;
              if (retries >= MAX_RETRIES) {
                // On final failure, create failed estimates for this sub-batch
                console.error(`Sub-batch estimation failed for category "${category.categoryName}" (batch ${batchIndex + 1}/${subBatches.length}) after ${MAX_RETRIES} retries:`, error);
                subBatchResults = formattedLineItems.map(item => ({
                  description: item.description,
                  categoryName: item.categoryName,
                  unitCost: null,
                  unit: item.unit || 'LS',
                  quantity: item.quantity || 1,
                  confidenceScore: 0,
                  explanation: 'AI estimation failed. Please estimate manually or retry.',
                  estimationMethod: 'failed',
                  failureReason: error.message,
                }));
              } else {
                // Exponential backoff: 300ms, 600ms, 1200ms
                const backoffMs = DELAY_BETWEEN_BATCHES_MS * Math.pow(2, retries);
                console.warn(`Rate limit hit for category "${category.categoryName}" (batch ${batchIndex + 1}/${subBatches.length}), retrying in ${backoffMs}ms (attempt ${retries + 1}/${MAX_RETRIES})`);
                await new Promise(resolve => setTimeout(resolve, backoffMs));
              }
            }
          }
        }

        // If timeout occurred, process items individually
        if (shouldFallbackToIndividual) {
          subBatchResults = [];
          for (const item of formattedLineItems) {
            const result = await estimateSingleItem(item);
            subBatchResults.push(result);

            // Update progress after each individual item
            completedItems++;
            if (onProgress) {
              onProgress({
                total: totalItems,
                completed: completedItems,
                currentCategory: category.categoryName,
                percentComplete: Math.round((completedItems / totalItems) * 100),
              });
            }

            // Small delay between individual requests
            await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES_MS));
          }
          // Don't add to completedItems again below since we already did it per-item
          categoryResults.push(...subBatchResults);
          continue; // Skip the normal accumulation logic below
        }

        // Accumulate sub-batch results
        if (subBatchResults) {
          categoryResults.push(...subBatchResults);
          completedItems += subBatchResults.length;

          // Update progress after each sub-batch (same category name shown)
          if (onProgress) {
            onProgress({
              total: totalItems,
              completed: completedItems,
              currentCategory: category.categoryName,
              percentComplete: Math.round((completedItems / totalItems) * 100),
            });
          }
        }

        // Delay between sub-batches (unless this is the last sub-batch of the last category)
        const isLastSubBatch = batchIndex === subBatches.length - 1;
        const isLastCategory = catIndex === categories.length - 1;
        if (!(isLastSubBatch && isLastCategory)) {
          await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES_MS));
        }
      }

      // Add all category results to overall results
      results.push(...categoryResults);

      // Notify caller of partial results (for skip functionality)
      // Include completed category count for progress animation
      if (onPartialResults) {
        onPartialResults([...results], catIndex + 1);
      }
    }

    // Final progress update
    if (onProgress) {
      onProgress({
        total: totalItems,
        completed: completedItems,
        currentCategory: null,
        percentComplete: 100,
      });
    }

    return results;
  },

  /**
   * Update a line item (with optional user adjustment reason)
   * @param {string} estimateId - Estimate UUID
   * @param {string} lineItemId - Line item UUID
   * @param {Object} updates - Updates to apply
   * @param {number} [updates.totalCost] - New total cost
   * @param {string} [updates.userAdjustmentReason] - Required explanation when changing AI estimate
   * @param {string} [updates.masterformatCode] - New MasterFormat code (reclassification)
   * @param {string} [updates.masterformatTitle] - Optional title for the new code
   * @param {boolean} [updates.persistMapping] - When true, save description -> code as a per-tenant mapping for future uploads
   * @param {string} [updates.sourceDescription] - Original source line-item description to use as the mapping key
   * @returns {Object} Updated line item with full context
   */
  updateLineItem: async (estimateId, lineItemId, updates) => {
    if (USE_MOCK_DATA) {
      return {
        lineItemId,
        estimateId,
        totalCost: updates.totalCost,
        costSource: updates.userAdjustmentReason ? 'user_adjusted' : 'manual',
        aiEstimatedCost: updates.totalCost * 0.95, // Mock original AI estimate
        aiExplanation: 'Mock AI explanation for this line item estimate.',
        userAdjustedCost: updates.userAdjustmentReason ? updates.totalCost : null,
        userAdjustmentReason: updates.userAdjustmentReason || null,
        userAdjustedAt: updates.userAdjustmentReason ? new Date().toISOString() : null,
        userAdjustedBy: updates.userAdjustmentReason ? 'mock@example.com' : null,
      };
    }
    return api.put(`/estimates/${estimateId}/line-items/${lineItemId}`, updates);
  },

  /**
   * Get a single line item with full AI and user adjustment context
   * @param {string} estimateId - Estimate UUID
   * @param {string} lineItemId - Line item UUID
   * @returns {Object} Line item with AI explanation and user adjustment info
   */
  getLineItem: async (estimateId, lineItemId) => {
    if (USE_MOCK_DATA) {
      return {
        lineItemId,
        estimateId,
        description: 'Mock line item',
        totalCost: 150000,
        costSource: 'ai',
        aiEstimatedCost: 150000,
        aiExplanation: 'Based on 5 similar line items in historical data...',
        aiConfidenceScore: 0.87,
        userAdjustedCost: null,
        userAdjustmentReason: null,
      };
    }
    return api.get(`/estimates/${estimateId}/line-items/${lineItemId}`);
  },

  /**
   * Search CSI MasterFormat codes for line item autocomplete
   * @param {string} query - Search query (can be empty for browse mode)
   * @param {Object} options - Optional filters
   * @param {number} options.limit - Max results (default 15)
   * @param {number} options.division - Filter by division number (e.g., 3 for Concrete)
   * @param {number} options.level - Filter by hierarchy level (1=division, 2=section, 3=subsection)
   * @param {string[]} options.excludeCodes - MasterFormat codes to exclude from results
   * @returns {Object} results - Array of matching MasterFormat items
   */
  searchMasterFormat: async (query, { limit = 15, division, level, excludeCodes } = {}) => {
    if (USE_MOCK_DATA) {
      // Mock MasterFormat search results - include divisions for category autocomplete
      const mockResults = [
        { code: '01 00 00', title: 'General Requirements', divisionNumber: 1, divisionTitle: 'General Requirements', level: 1 },
        { code: '03 00 00', title: 'Concrete', divisionNumber: 3, divisionTitle: 'Concrete', level: 1 },
        { code: '03 30 00', title: 'Cast-in-Place Concrete', divisionNumber: 3, divisionTitle: 'Concrete', level: 2 },
        { code: '03 31 00', title: 'Structural Concrete', divisionNumber: 3, divisionTitle: 'Concrete', level: 3 },
        { code: '03 35 00', title: 'Concrete Finishing', divisionNumber: 3, divisionTitle: 'Concrete', level: 3 },
        { code: '05 00 00', title: 'Metals', divisionNumber: 5, divisionTitle: 'Metals', level: 1 },
        { code: '05 12 00', title: 'Structural Steel Framing', divisionNumber: 5, divisionTitle: 'Metals', level: 3 },
        { code: '09 00 00', title: 'Finishes', divisionNumber: 9, divisionTitle: 'Finishes', level: 1 },
        { code: '09 29 00', title: 'Gypsum Board', divisionNumber: 9, divisionTitle: 'Finishes', level: 3 },
        { code: '09 91 00', title: 'Painting', divisionNumber: 9, divisionTitle: 'Finishes', level: 3 },
        { code: '23 00 00', title: 'Heating, Ventilating, and Air Conditioning (HVAC)', divisionNumber: 23, divisionTitle: 'HVAC', level: 1 },
        { code: '23 31 00', title: 'HVAC Ducts and Casings', divisionNumber: 23, divisionTitle: 'HVAC', level: 3 },
        { code: '26 00 00', title: 'Electrical', divisionNumber: 26, divisionTitle: 'Electrical', level: 1 },
        { code: '26 51 00', title: 'Interior Lighting', divisionNumber: 26, divisionTitle: 'Electrical', level: 3 },
      ].filter(item => {
        // Apply division filter
        if (division && item.divisionNumber !== division) return false;
        // Apply level filter
        if (level && item.level !== level) return false;
        // Apply exclude filter
        if (excludeCodes && excludeCodes.includes(item.code)) return false;
        // Apply search query (if provided)
        if (query) {
          return item.title.toLowerCase().includes(query.toLowerCase()) ||
            item.code.includes(query);
        }
        return true;
      }).slice(0, limit);

      return {
        success: true,
        data: {
          results: mockResults,
          count: mockResults.length,
        },
      };
    }

    const params = new URLSearchParams();
    params.append('q', query || '');
    if (limit) params.append('limit', limit);
    if (division !== undefined && division !== null) params.append('division', division);
    if (level) params.append('level', level);
    // Note: excludeCodes filtering is handled client-side in MasterFormatAutocomplete

    return api.get(`/masterformat/search?${params.toString()}`);
  },

  /**
   * Get similar line items by MasterFormat code
   * Returns line items with the same MasterFormat code from similar historical projects
   * @param {string} code - MasterFormat code (e.g., "03 35 00")
   * @param {Object} options - Optional filters
   * @param {number} options.limit - Max results (default 5)
   * @returns {Object} results - Array of similar line items
   */
  getSimilarLineItemsByCode: async (code, { limit = 5 } = {}) => {
    if (USE_MOCK_DATA) {
      // Mock response with realistic historical data
      return {
        results: [
          {
            lineItemId: 'mock-sim-1',
            estimateId: 'est-001',
            projectName: 'Amazon Warehouse - Atlanta',
            description: 'Concrete Finishing',
            quantity: 85000,
            unit: 'SF',
            unitCost: 11.80,
            totalCost: 1003000,
            similarityScore: 0.92,
            jobType: 'Warehouse',
            location: 'Atlanta, GA',
          },
          {
            lineItemId: 'mock-sim-2',
            estimateId: 'est-002',
            projectName: 'Home Depot Distribution Center',
            description: 'Concrete Finishing',
            quantity: 120000,
            unit: 'SF',
            unitCost: 12.25,
            totalCost: 1470000,
            similarityScore: 0.89,
            jobType: 'Warehouse',
            location: 'Phoenix, AZ',
          },
          {
            lineItemId: 'mock-sim-3',
            estimateId: 'est-003',
            projectName: 'Target Store - Denver',
            description: 'Concrete Finishing',
            quantity: 45000,
            unit: 'SF',
            unitCost: 13.50,
            totalCost: 607500,
            similarityScore: 0.85,
            jobType: 'Retail',
            location: 'Denver, CO',
          },
          {
            lineItemId: 'mock-sim-4',
            estimateId: 'est-004',
            projectName: 'Office Building - Seattle',
            description: 'Concrete Finishing',
            quantity: 35000,
            unit: 'SF',
            unitCost: 14.20,
            totalCost: 497000,
            similarityScore: 0.78,
            jobType: 'Office',
            location: 'Seattle, WA',
          },
          {
            lineItemId: 'mock-sim-5',
            estimateId: 'est-005',
            projectName: 'Retail Center - Dallas',
            description: 'Concrete Finishing',
            quantity: 28000,
            unit: 'SF',
            unitCost: 11.50,
            totalCost: 322000,
            similarityScore: 0.75,
            jobType: 'Retail',
            location: 'Dallas, TX',
          },
        ].slice(0, limit),
      };
    }

    const params = new URLSearchParams();
    params.append('code', code);
    if (limit) params.append('limit', limit);

    const response = await api.get(`/line-items/similar-by-code?${params.toString()}`);
    const data = response.data || response;
    return {
      results: convertKeysToCamelCase(data.results || []),
      count: data.count,
      code: data.code,
    };
  },

  /**
   * Get line items by their IDs (batch fetch)
   * Used to fetch the exact similar items that were stored during AI estimation
   * @param {string[]} lineItemIds - Array of line item UUIDs
   * @returns {Object} lineItems - Array of line item details
   */
  getLineItemsByIds: async (lineItemIds) => {
    if (USE_MOCK_DATA) {
      // Mock response with realistic historical data
      return {
        lineItems: lineItemIds.map((id, idx) => ({
          lineItemId: id,
          projectName: `Mock Project ${idx + 1}`,
          description: `Mock line item ${idx + 1}`,
          quantity: 50000 + (idx * 10000),
          unit: 'SF',
          unitCost: 10.0 + idx,
          totalCost: (50000 + (idx * 10000)) * (10.0 + idx),
          costSource: idx % 2 === 0 ? 'ai' : 'user_adjusted',
          aiEstimatedCost: idx % 2 === 1 ? (50000 + (idx * 10000)) * (9.5 + idx) : null,
          aiConfidenceScore: idx % 2 === 0 ? 0.85 - (idx * 0.05) : null,
          userAdjustedCost: idx % 2 === 1 ? (50000 + (idx * 10000)) * (10.0 + idx) : null,
          userAdjustmentReason: idx % 2 === 1 ? 'Market rates higher than expected' : null,
          projectDate: new Date(2024, idx % 12, 15).toISOString().split('T')[0],
          jobType: ['Retail', 'Warehouse', 'Office', 'Mixed Use'][idx % 4],
          location: ['Atlanta, GA', 'Denver, CO', 'Seattle, WA', 'Dallas, TX'][idx % 4],
          squareFootage: 50000 + (idx * 15000),
          categoryName: ['Concrete', 'Metals', 'Finishes', 'Sitework'][idx % 4],
        })),
      };
    }

    const response = await api.post('/line-items/batch', { lineItemIds });
    return convertKeysToCamelCase(response.data);
  },

  // =========================================================================
  // SUPPORTING DOCUMENTS
  // =========================================================================

  /**
   * Get a presigned URL for uploading a supporting document
   * @param {string} estimateId - Estimate UUID
   * @param {Object} fileInfo - File information
   * @param {string} fileInfo.fileName - Original file name
   * @param {string} fileInfo.contentType - MIME type
   * @param {number} fileInfo.fileSize - File size in bytes
   * @param {string} [fileInfo.categoryId] - Document category UUID
   * @returns {Promise<{documentId: string, uploadUrl: string, s3Key: string, expiresIn: number}>}
   */
  getDocumentUploadUrl: async (estimateId, { fileName, contentType, fileSize, categoryId }) => {
    if (USE_MOCK_DATA) {
      const documentId = `doc-${Date.now()}`;
      return {
        documentId,
        uploadUrl: `https://mock-bucket.s3.amazonaws.com/mock-upload-url?documentId=${documentId}`,
        s3Key: `uploads/mock-tenant/supporting/${estimateId}/${documentId}_${fileName}`,
        expiresIn: 3600,
      };
    }

    return api.post(`/estimates/${estimateId}/documents/upload`, {
      fileName,
      contentType,
      fileSize,
      categoryId,
    });
  },

  /**
   * Upload a supporting document to S3 using presigned URL
   * @param {string} estimateId - Estimate UUID
   * @param {File} file - File to upload
   * @param {string} [categoryId] - Document category UUID
   * @param {Function} [onProgress] - Progress callback (0-100)
   * @returns {Promise<{documentId: string, status: string}>}
   */
  uploadSupportingDocument: async (estimateId, file, onProgress) => {
    if (USE_MOCK_DATA) {
      // Simulate upload progress
      for (let i = 0; i <= 100; i += 20) {
        if (onProgress) onProgress(i);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return {
        documentId: `doc-${Date.now()}`,
        status: 'processing',
        fileName: file.name,
      };
    }

    // Get presigned URL
    const { documentId, uploadUrl } = await estimatesApi.getDocumentUploadUrl(estimateId, {
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      fileSize: file.size,
    });

    // Upload to S3
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed with status ${response.status}`);
    }

    // Count pages so the page-budget allocator can fan out at start-processing time.
    let totalPages = null;
    try {
      const result = await estimatesApi.countDocumentPages(estimateId, documentId);
      totalPages = result?.totalPages ?? null;
    } catch (err) {
      console.warn('Failed to count document pages:', err);
    }

    return {
      documentId,
      status: 'uploaded',
      fileName: file.name,
      totalPages,
    };
  },

  /**
   * Trigger document processing pipeline (classification + fact extraction).
   * Normally invoked indirectly via startDocumentProcessing — this direct
   * endpoint is kept for backwards compatibility and admin tools.
   * @param {string} estimateId - Estimate UUID
   * @param {string} documentId - Document UUID
   * @returns {Promise<{documentId: string, status: string, pipeline: string}>}
   */
  processDocument: async (estimateId, documentId) => {
    if (USE_MOCK_DATA) {
      return { documentId, status: 'processing', pipeline: 'classify -> extract' };
    }

    const response = await api.post(`/estimates/${estimateId}/documents/${documentId}/process`);
    return response.data || response;
  },

  /**
   * Count the number of pages in an uploaded document so the page-budget
   * allocator can divide work across docs at start-processing time.
   * @param {string} estimateId
   * @param {string} documentId
   * @returns {Promise<{documentId: string, totalPages: number, uploadStatus: string}>}
   */
  countDocumentPages: async (estimateId, documentId) => {
    if (USE_MOCK_DATA) {
      return { documentId, totalPages: 1, uploadStatus: 'uploaded' };
    }
    const response = await api.post(
      `/estimates/${estimateId}/documents/${documentId}/count-pages`
    );
    return response.data || response;
  },

  /**
   * Allocate the estimate's remaining page budget across uploaded docs and
   * trigger processing for each. Called when the user clicks Continue / Process.
   * @param {string} estimateId
   * @returns {Promise<{started: number, maxPages: number, pagesUsed: number,
   *                    pagesRemaining: number, documents: Array}>}
   */
  startDocumentProcessing: async (estimateId) => {
    if (USE_MOCK_DATA) {
      return { started: 0, maxPages: 25, pagesUsed: 0, pagesRemaining: 25, documents: [] };
    }
    const response = await api.post(`/estimates/${estimateId}/documents/start-processing`);
    return response.data || response;
  },

  /**
   * Read the page-budget meter for an estimate. Used by the uploader to
   * gate the "Add document" CTA when the budget is exhausted.
   * @param {string} estimateId
   * @returns {Promise<{maxPages: number, pagesUsed: number, pagesRemaining: number}>}
   */
  getEstimatePageBudget: async (estimateId) => {
    if (USE_MOCK_DATA) {
      return { maxPages: 25, pagesUsed: 0, pagesRemaining: 25 };
    }
    const response = await api.get(`/estimates/${estimateId}/page-budget`);
    return response.data || response;
  },

  /**
   * List all supporting documents for an estimate
   * @param {string} estimateId - Estimate UUID
   * @returns {Promise<{documents: Array, count: number}>}
   */
  listEstimateDocuments: async (estimateId) => {
    if (USE_MOCK_DATA) {
      return {
        documents: [
          {
            documentId: 'doc-001',
            estimateId,
            categoryId: 'cat-001',
            categoryName: 'Blueprints & Plans',
            categoryCsiDivisions: [1, 2, 3, 4, 5, 6, 7, 8, 9],
            fileName: 'doc-001_floor_plan.pdf',
            originalFileName: 'floor_plan.pdf',
            contentType: 'application/pdf',
            fileSizeBytes: 2457600,
            uploadStatus: 'uploaded',
            createdAt: '2024-01-20T14:30:00Z',
          },
          {
            documentId: 'doc-002',
            estimateId,
            categoryId: 'cat-002',
            categoryName: 'Geological Survey',
            categoryCsiDivisions: [2, 3, 31, 32, 33],
            fileName: 'doc-002_soil_report.pdf',
            originalFileName: 'soil_report.pdf',
            contentType: 'application/pdf',
            fileSizeBytes: 1843200,
            uploadStatus: 'uploaded',
            createdAt: '2024-01-20T14:35:00Z',
          },
        ],
        count: 2,
      };
    }

    return api.get(`/estimates/${estimateId}/documents`);
  },

  // =========================================================================
  // ESTIMATE PROCESSING STATUS
  // =========================================================================

  /**
   * Get the processing status of an estimate (for polling during CSV upload)
   * @param {string} estimateId - Estimate UUID
   * @returns {Promise<{estimateId: string, status: string, progress: number, projectName?: string, categoriesFound?: number, lineItemsFound?: number, error?: string}>}
   */
  getEstimateStatus: async (estimateId) => {
    if (USE_MOCK_DATA) {
      // Mock processing status - simulate progress
      return {
        estimateId,
        status: 'completed',
        progress: 100,
        projectName: 'Imported Estimate',
        categoriesFound: 12,
        lineItemsFound: 145,
        error: null,
      };
    }

    const response = await api.get(`/estimates/${estimateId}/status`);
    return response.data || response;
  },

  // =========================================================================
  // DOCUMENT PROCESSING STATUS
  // =========================================================================

  /**
   * Get the processing status of all documents for an estimate
   * @param {string} estimateId - Estimate UUID
   * @returns {Promise<{total: number, completed: number, processing: number, failed: number, documents: Array}>}
   */
  getDocumentsProcessingStatus: async (estimateId) => {
    if (USE_MOCK_DATA) {
      // Mock document processing status
      return {
        total: 2,
        completed: 2,
        processing: 0,
        failed: 0,
        documents: [
          {
            documentId: 'doc-001',
            fileName: 'floor_plan.pdf',
            status: 'processed',
            divisionsExtracted: [1, 2, 3],
          },
          {
            documentId: 'doc-002',
            fileName: 'soil_report.pdf',
            status: 'processed',
            divisionsExtracted: [2, 3, 31],
          },
        ],
      };
    }

    const response = await api.get(`/estimates/${estimateId}/documents/processing-status`);
    return response.data || response;
  },

  // =========================================================================
  // DOCUMENT FACTS
  // =========================================================================

  /**
   * Get extracted facts for a specific document
   * @param {string} estimateId - Estimate UUID
   * @param {string} documentId - Document UUID
   * @returns {Promise<{documentId: string, divisions: Array<{divisionNumber: number, divisionTitle: string, facts: Object, confidence: number}>}>}
   */
  getDocumentFacts: async (estimateId, documentId) => {
    if (USE_MOCK_DATA) {
      // Mock extracted facts
      return {
        documentId,
        divisions: [
          {
            divisionNumber: 3,
            divisionTitle: 'Concrete',
            facts: {
              foundation_type: 'spread_footing',
              concrete_strength_psi: '4000',
              slab_thickness_in: 6,
            },
            factId: 'fact-001',
            confidence: 0.85,
            extractionMethod: 'text_haiku',
          },
          {
            divisionNumber: 31,
            divisionTitle: 'Earthwork',
            facts: {
              soil_type: 'clay',
              rock_encountered: false,
            },
            factId: 'fact-002',
            confidence: 0.78,
            extractionMethod: 'text_haiku',
          },
        ],
      };
    }

    const response = await api.get(`/estimates/${estimateId}/documents/${documentId}/facts`);
    return response.data || response;
  },

  /**
   * Update extracted facts for a document
   * @param {string} estimateId - Estimate UUID
   * @param {string} documentId - Document UUID
   * @param {Array<{divisionNumber: number, facts: Object}>} divisions - Updated facts by division
   * @returns {Promise<{updatedDivisions: number[], validationErrors: string[]}>}
   */
  updateDocumentFacts: async (estimateId, documentId, divisions) => {
    if (USE_MOCK_DATA) {
      // Mock update response
      return {
        updatedDivisions: divisions.map((d) => d.divisionNumber),
        validationErrors: [],
      };
    }

    const response = await api.put(`/estimates/${estimateId}/documents/${documentId}/facts`, {
      divisions,
    });
    return response.data || response;
  },

  /**
   * Delete a supporting document
   * @param {string} estimateId - Estimate UUID
   * @param {string} documentId - Document UUID
   * @returns {Promise<{deleted: boolean, documentId: string}>}
   */
  deleteDocument: async (estimateId, documentId) => {
    if (USE_MOCK_DATA) {
      return { deleted: true, documentId };
    }

    return api.delete(`/estimates/${estimateId}/documents/${documentId}`);
  },

  /**
   * Get a presigned download URL for a document
   * @param {string} estimateId - Estimate UUID
   * @param {string} documentId - Document UUID
   * @returns {Promise<{downloadUrl: string, fileName: string, contentType: string, expiresIn: number}>}
   */
  getDocumentDownloadUrl: async (estimateId, documentId) => {
    if (USE_MOCK_DATA) {
      return {
        downloadUrl: `https://mock-bucket.s3.amazonaws.com/mock-download?documentId=${documentId}`,
        fileName: 'mock-document.pdf',
        contentType: 'application/pdf',
        expiresIn: 3600,
      };
    }

    return api.get(`/estimates/${estimateId}/documents/${documentId}/download`);
  },

  /**
   * Search for location suggestions (autocomplete).
   */
  searchLocationSuggestions: async (query) => {
    if (!query || query.length < 2) return { suggestions: [] };
    if (USE_MOCK_DATA) {
      return {
        suggestions: [
          { text: `${query}, OH, USA`, placeId: 'mock-1' },
          { text: `${query}, CO, USA`, placeId: 'mock-2' },
        ],
      };
    }
    const response = await api.get(`/locations/suggestions?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  /**
   * Validate a location string against AWS Location Service.
   */
  validateLocation: async (text) => {
    if (!text) return { valid: false };
    if (USE_MOCK_DATA) {
      return {
        valid: true,
        label: text,
        municipality: text.split(',')[0]?.trim() || '',
        region: text.split(',')[1]?.trim() || '',
      };
    }
    const response = await api.post('/locations/validate', { text });
    return response.data;
  },
};

export default estimatesApi;
